import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MarketInfoApi, { MarketInfoImportModel } from '../MarketInfo/api/MarketInfoApi';
import Utility from '../../app/utility';
import './BulkImportMarketInfo.scss';

const TYPE_COLORS: Record<string, 'primary' | 'success' | 'warning' | 'secondary' | 'error'> = {
    TCT:    'primary',
    TC:     'success',
    Voy:    'warning',
    COA:    'secondary',
    Period: 'error',
};

const BulkImportMarketInfo: React.FunctionComponent = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [importData, setImportData] = useState<MarketInfoImportModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isValidated, setIsValidated] = useState<boolean>(false);
    const [skipDuplicates, setSkipDuplicates] = useState<boolean>(true);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setImportData([]);
            setIsValidated(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            Utility.Alert.Warning('Please select an Excel file');
            return;
        }
        setIsLoading(true);
        try {
            const response = await MarketInfoApi.ImportMarketInfoFromExcel(selectedFile);
            if (response.success && response.data) {
                setImportData(response.data);
                setIsValidated(false);
                Utility.Alert.Success(`Imported ${response.data.length} records`);
            } else {
                Utility.Alert.Error('Failed to import file');
            }
        } catch (error: any) {
            Utility.Alert.Error(error.response?.data?.message || 'Error importing file');
        } finally {
            setIsLoading(false);
        }
    };

    const handleValidate = async () => {
        if (importData.length === 0) {
            Utility.Alert.Warning('No data to validate');
            return;
        }
        setIsLoading(true);
        try {
            const response = await MarketInfoApi.ValidateMarketInfoImportData(importData);
            if (response.success && response.data) {
                setImportData(response.data);
                const hasErrors = response.data.some(x => x.validationErrors && x.validationErrors.length > 0);
                setIsValidated(!hasErrors);
                if (hasErrors) {
                    Utility.Alert.Warning('Please fix validation errors before saving');
                } else {
                    const dupCount = response.data.filter(x => x.isDuplicate).length;
                    if (dupCount > 0) {
                        Utility.Alert.Warning(`${dupCount} duplicate record(s) detected. Review before saving.`);
                    } else {
                        Utility.Alert.Success('All records validated successfully');
                    }
                }
            }
        } catch (error: any) {
            Utility.Alert.Error(error.response?.data?.message || 'Error validating data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!isValidated) {
            Utility.Alert.Warning('Please validate data before saving');
            return;
        }
        setIsLoading(true);
        try {
            const dataToSave = skipDuplicates
                ? importData.filter(x => !x.isDuplicate)
                : importData;

            if (dataToSave.length === 0) {
                Utility.Alert.Warning('No records to save (all were duplicates)');
                setIsLoading(false);
                return;
            }

            const response = await MarketInfoApi.SaveMarketInfoImportData(dataToSave);
            if (response.success) {
                Utility.Alert.Success(`Successfully saved ${response.data} market info record(s)`);
                setImportData([]);
                setSelectedFile(null);
                setIsValidated(false);
                const fileInput = document.getElementById('mi-file-input') as HTMLInputElement;
                if (fileInput) fileInput.value = '';
            }
        } catch (error: any) {
            Utility.Alert.Error(error.response?.data?.message || 'Error saving data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldChange = (index: number, field: keyof MarketInfoImportModel, value: string) => {
        const newData = [...importData];
        (newData[index] as any)[field] = value;
        setImportData(newData);
        setIsValidated(false);
    };

    const handleDeleteRow = (index: number) => {
        setImportData(importData.filter((_, i) => i !== index));
        setIsValidated(false);
    };

    const handleClear = () => {
        setImportData([]);
        setSelectedFile(null);
        setIsValidated(false);
        const fileInput = document.getElementById('mi-file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
    };

    const hasFieldError = (item: MarketInfoImportModel, keywords: string[]): boolean => {
        if (!item.validationErrors || item.validationErrors.length === 0) return false;
        return item.validationErrors.some(e => keywords.some(k => e.toLowerCase().includes(k)));
    };

    const getFieldError = (item: MarketInfoImportModel, keywords: string[]): string => {
        if (!item.validationErrors) return '';
        return item.validationErrors.find(e => keywords.some(k => e.toLowerCase().includes(k))) || '';
    };

    const duplicateCount = importData.filter(x => x.isDuplicate).length;

    return (
        <Box className="bulk-import-market-info-container" sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Bulk Import Market Info
            </Typography>

            {/* File Upload Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <input
                        id="mi-file-input"
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        type="file"
                        onChange={handleFileSelect}
                    />
                    <label htmlFor="mi-file-input">
                        <Button variant="outlined" component="span" startIcon={<UploadFileIcon />}>
                            Select Excel File
                        </Button>
                    </label>
                    {selectedFile && (
                        <Typography variant="body2">{selectedFile.name}</Typography>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        color={!selectedFile || isLoading ? 'info' : 'primary'}
                        disabled={!selectedFile || isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Upload & Import'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleClear}
                        startIcon={<ClearIcon />}
                        disabled={isLoading}
                    >
                        Clear
                    </Button>
                </Box>
            </Paper>

            {/* Action Bar */}
            {importData.length > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button variant="outlined" onClick={handleValidate} disabled={isLoading}>
                            Validate Data
                        </Button>
                        <Button
                            variant="contained"
                            color={!isValidated || isLoading ? 'info' : 'primary'}
                            onClick={handleSave}
                            disabled={!isValidated || isLoading}
                        >
                            Save to Database
                        </Button>
                        {duplicateCount > 0 && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={skipDuplicates}
                                        onChange={e => setSkipDuplicates(e.target.checked)}
                                        color="warning"
                                    />
                                }
                                label={`Skip ${duplicateCount} duplicate(s)`}
                            />
                        )}
                        <Typography variant="body2" sx={{ ml: 'auto' }}>
                            {importData.length} records loaded
                            {duplicateCount > 0 && ` · ${duplicateCount} duplicate(s)`}
                        </Typography>
                    </Box>
                    {duplicateCount > 0 && (
                        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 1 }}>
                            {duplicateCount} record(s) appear to already exist in the database (same Type + Vessel + Ports).
                            {skipDuplicates ? ' They will be skipped on save.' : ' They will be included on save.'}
                        </Alert>
                    )}
                </Paper>
            )}

            {/* Preview Table */}
            {importData.length > 0 && (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Row</strong></TableCell>
                                <TableCell><strong>Vessel Name</strong></TableCell>
                                <TableCell><strong>Size</strong></TableCell>
                                <TableCell><strong>Type</strong></TableCell>
                                <TableCell><strong>Cargo Type</strong></TableCell>
                                <TableCell><strong>Load Port</strong></TableCell>
                                <TableCell><strong>Disport</strong></TableCell>
                                <TableCell><strong>Own Rate</strong></TableCell>
                                <TableCell><strong>Chrs Rate</strong></TableCell>
                                <TableCell><strong>Owner</strong></TableCell>
                                <TableCell><strong>Charterer</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {importData.map((item, index) => (
                                <TableRow
                                    key={index}
                                    className={item.isDuplicate ? 'duplicate-row' : ''}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="caption">{item.rowRef}</Typography>
                                            {item.isDuplicate && (
                                                <Tooltip title="Duplicate detected">
                                                    <WarningAmberIcon color="warning" fontSize="small" />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={item.vesselName || ''}
                                            onChange={e => handleFieldChange(index, 'vesselName', e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={item.size ?? ''}
                                            onChange={e => handleFieldChange(index, 'size', e.target.value)}
                                            sx={{ width: 80 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <TextField
                                                size="small"
                                                value={item.type || ''}
                                                onChange={e => handleFieldChange(index, 'type', e.target.value)}
                                                error={hasFieldError(item, ['type'])}
                                                helperText={getFieldError(item, ['type'])}
                                                sx={{ width: 80 }}
                                            />
                                            {item.type && !hasFieldError(item, ['type']) && (
                                                <Chip
                                                    label={item.type}
                                                    color={TYPE_COLORS[item.type] ?? 'default'}
                                                    size="small"
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={item.cargoTypeName || ''}
                                            onChange={e => handleFieldChange(index, 'cargoTypeName', e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={item.loadPortName || ''}
                                            onChange={e => handleFieldChange(index, 'loadPortName', e.target.value)}
                                            error={hasFieldError(item, ['load port'])}
                                            helperText={getFieldError(item, ['load port'])}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={item.disportName || ''}
                                            onChange={e => handleFieldChange(index, 'disportName', e.target.value)}
                                            error={hasFieldError(item, ['discharge port'])}
                                            helperText={getFieldError(item, ['discharge port'])}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={item.ownerRate ?? ''}
                                            onChange={e => handleFieldChange(index, 'ownerRate', e.target.value)}
                                            sx={{ width: 90 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={item.chartererRate ?? ''}
                                            onChange={e => handleFieldChange(index, 'chartererRate', e.target.value)}
                                            sx={{ width: 90 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={item.ownerName || ''}
                                            onChange={e => handleFieldChange(index, 'ownerName', e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={item.chartererName || ''}
                                            onChange={e => handleFieldChange(index, 'chartererName', e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {item.validationErrors && item.validationErrors.length > 0 && (
                                                <Tooltip title={item.validationErrors.join(' | ')}>
                                                    <Chip label={`${item.validationErrors.length} error(s)`} color="error" size="small" />
                                                </Tooltip>
                                            )}
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => handleDeleteRow(index)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Empty State */}
            {importData.length === 0 && !isLoading && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No data loaded. Select and upload an Excel file to begin.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default BulkImportMarketInfo;
